import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
// TODO(icons): approx — was Lucide UploadCloudIcon, UploadIcon
import { FileIcon, CloudIcon, FileUploadIcon, CloseIcon } from '@/src/icons'

import { cn } from '@/registry/reapit/lib/utils'

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------

const dropzoneVariants = cva(
  'relative flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-input bg-background px-6 py-4 text-center transition-colors',
  {
    variants: {
      size: {
        large: 'min-h-32',
        medium: 'min-h-24',
        small: 'min-h-16',
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  },
)

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FileUploaderProps {
  size?: 'small' | 'medium' | 'large'
  disabled?: boolean
  error?: boolean
  multiple?: boolean
  accept?: string
  onFilesChange?: (files: File[]) => void
  files?: File[]
  onRemoveFile?: (file: File, index: number) => void
  className?: string
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace FileUploader {
  export type Props = FileUploaderProps
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFileSize(bytes: number): string {
  if (bytes >= 1_048_576) {
    return `${(bytes / 1_048_576).toFixed(1)} MB`
  }
  return `${Math.round(bytes / 1024)} KB`
}

// ---------------------------------------------------------------------------
// FileUploaderDropzone
// ---------------------------------------------------------------------------

interface DropzoneProps extends VariantProps<typeof dropzoneVariants> {
  disabled?: boolean
  error?: boolean
  multiple?: boolean
  accept?: string
  onFilesChange?: (files: File[]) => void
  className?: string
}

function FileUploaderDropzone({
  size = 'medium',
  disabled = false,
  error = false,
  multiple = false,
  accept,
  onFilesChange,
  className,
}: DropzoneProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = React.useState(false)

  function handleClick() {
    if (!disabled) {
      inputRef.current?.click()
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    if (!disabled) setIsDragging(true)
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    if (disabled) return
    const droppedFiles = Array.from(e.dataTransfer.files)
    if (droppedFiles.length > 0) {
      onFilesChange?.(droppedFiles)
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedFiles = Array.from(e.target.files ?? [])
    if (selectedFiles.length > 0) {
      onFilesChange?.(selectedFiles)
    }
    // Reset the input so the same file can be re-selected
    e.target.value = ''
  }

  return (
    <div
      data-slot="file-uploader-dropzone"
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        dropzoneVariants({ size }),
        'hover:border-primary/50 hover:bg-accent/50',
        isDragging && 'border-primary bg-accent/50',
        error && 'border-destructive',
        disabled && 'pointer-events-none opacity-50',
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        onChange={handleInputChange}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
      />

      {size === 'large' && (
        <>
          <FileUploadIcon size="xl" className="text-muted-foreground" aria-hidden />
          {/* DS: 4px gap between headline and sub-label — gap-0.5 (2px) was off-grid; use gap-1 (4px) */}
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium text-foreground">Upload files</p>
            <p className="text-xs text-muted-foreground">or drag and drop</p>
          </div>
        </>
      )}

      {size === 'medium' && (
        <>
          <FileUploadIcon size="lg" className="text-muted-foreground" aria-hidden />
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">Upload</span> or drag and drop
          </p>
        </>
      )}

      {size === 'small' && (
        <div className="flex items-center gap-2">
          <CloudIcon size="sm" className="shrink-0 text-muted-foreground" aria-hidden />
          <p className="text-xs text-muted-foreground">Click or drag to upload</p>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// FileUploaderFileList
// ---------------------------------------------------------------------------

interface FileListProps {
  files: File[]
  onRemoveFile?: (file: File, index: number) => void
  className?: string
}

function FileUploaderFileList({ files, onRemoveFile, className }: FileListProps) {
  if (files.length === 0) return null

  return (
    <ul
      data-slot="file-uploader-file-list"
      className={cn('mt-2 flex flex-col gap-1', className)}
      aria-label="Selected files"
    >
      {files.map((file, index) => (
        <li
          key={`${file.name}-${index}`}
          data-slot="file-uploader-file-item"
          className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm"
        >
          <FileIcon size="sm" className="shrink-0 text-muted-foreground" aria-hidden />
          <span className="min-w-0 flex-1 truncate font-medium text-foreground">{file.name}</span>
          <span className="shrink-0 text-xs text-muted-foreground">{formatFileSize(file.size)}</span>
          {onRemoveFile && (
            <button
              type="button"
              onClick={() => onRemoveFile(file, index)}
              className="ml-1 shrink-0 rounded-sm p-0.5 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`Remove ${file.name}`}
            >
              <CloseIcon size="sm" aria-hidden />
            </button>
          )}
        </li>
      ))}
    </ul>
  )
}

// ---------------------------------------------------------------------------
// FileUploader (composed)
// ---------------------------------------------------------------------------

function FileUploader({
  size = 'medium',
  disabled = false,
  error = false,
  multiple = false,
  accept,
  onFilesChange,
  files = [],
  onRemoveFile,
  className,
}: FileUploaderProps) {
  return (
    <div data-slot="file-uploader" className={cn('flex flex-col', className)}>
      <FileUploaderDropzone
        size={size}
        disabled={disabled}
        error={error}
        multiple={multiple}
        accept={accept}
        onFilesChange={onFilesChange}
      />
      <FileUploaderFileList files={files} onRemoveFile={onRemoveFile} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export { FileUploader, FileUploaderDropzone, FileUploaderFileList }
